import React from 'react';
import { Box, Paper, Typography, Button, Alert, LinearProgress } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const UploadProgress = ({ uploadedFiles, onViewDashboard, isProcessing }) => {
  const dreUploaded = uploadedFiles?.dre;
  const balancoUploaded = uploadedFiles?.balanco;
  const bothUploaded = dreUploaded && balancoUploaded;
  const anyUploaded = dreUploaded || balancoUploaded;

  const getActiveStep = () => {
    if (bothUploaded) return 2;
    if (dreUploaded || balancoUploaded) return 1;
    return 0;
  };

  const activeStep = getActiveStep();
  const progress = (activeStep / 2) * 100;

  return (
    <AnimatePresence>
      {(dreUploaded || balancoUploaded) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4 }}
        >
          <Paper
            variant="outlined"
            sx={{ p: 3, mb: 3 }}
          >
            {/* Header */}
            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="h6">Dados reconhecidos</Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  {activeStep} de 2 demonstrativos
                </Typography>
              </Box>

              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  '& .MuiLinearProgress-bar': {
                    bgcolor: bothUploaded ? 'success.main' : 'primary.main',
                  },
                }}
              />

              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {bothUploaded
                  ? 'DRE e balanço disponíveis. Pronto para gerar o dashboard.'
                  : `${dreUploaded ? 'DRE disponível' : 'Balanço disponível'} · o diagnóstico parcial já pode ser calculado`}
              </Typography>
            </Box>

            {/* Instructions */}
            {!bothUploaded && anyUploaded && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <Alert severity="info" sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' } }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {dreUploaded && !balancoUploaded
                          ? 'Envie o balanço no mesmo campo para completar a análise'
                          : 'Envie a DRE no mesmo campo para completar a análise'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Ou visualize com os dados atuais (algumas análises ficarão incompletas)
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="medium"
                      endIcon={<ArrowForwardIcon />}
                      onClick={onViewDashboard}
                      disabled={isProcessing}
                      sx={{
                        ml: { xs: 0, sm: 2 },
                        minWidth: 140,
                        fontWeight: 600,
                      }}
                    >
                      {isProcessing ? 'Processando...' : 'Ver Parcial'}
                    </Button>
                  </Box>
                </Alert>
              </motion.div>
            )}

            {/* Success & Action */}
            <AnimatePresence>
              {bothUploaded && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <Alert
                    severity="success"
                    icon={<CheckCircleIcon />}
                    sx={{
                      mt: 3,
                      '& .MuiAlert-message': {
                        width: '100%',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' } }}>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          DRE e balanço foram importados
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Os dados estão prontos. Gere o dashboard para ver a análise.
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        endIcon={<ArrowForwardIcon />}
                        onClick={onViewDashboard}
                        disabled={isProcessing}
                        sx={{ ml: { xs: 0, sm: 2 }, minWidth: 160, flexShrink: 0 }}
                      >
                        {isProcessing ? 'Processando...' : 'Ver Dashboard'}
                      </Button>
                    </Box>
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>
          </Paper>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UploadProgress;
